import { Layout, Table, Typography, Col } from "antd";
import React, { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import axios from "api/axios";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import { formatDataGeneral, getAttributeValue, getFileLinkIfExist } from "utils/utils";
import { transformDpatTableRows, rowsHaveAllEvidenceLinks, scoreOnlyIfRowValuesProvided } from "utils/renderDpatEvidenceLink";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";

const BusinessCommunityEngagement = forwardRef(({
    year,
    district,
    hideComment,
    savedAssessmentData,
    assessmentStartLoading,
    isAssessmentStarted,
}, ref) => {

    const [data, setData] = useState([]);
    const [report, setReport] = useState([]);
    const [scorei, setScorei] = useState(0);
    const [scoreii, setScoreii] = useState(0);
    const [maxScore, setMaxScore] = useState(2);
    const { Header, Content } = Layout;
    const { Title, Text } = Typography;

    useImperativeHandle(ref, () => ({
        getData: () => ({
            indicator: "SDI6",
            area: "Local Economic Development (LED)",
            maxScore,
            data,
            report,
            scorei,
            scoreii
        }),
    }));

    const DPAT_SLUG = "sdi6.0-6.4";

    const loadFromDhis2 = useCallback(() => {
        axios
            .get(`/tracker/trackedEntities?orgUnit=${district}&program=yiLy1Tiyjng&startDate=${year}-01-01&endDate=${year}-12-31`)
            .then(result => {
                if (result.data.instances.length > 0) {

                    axios
                        .get(`/tracker/events?program=yiLy1Tiyjng&orgUnit=${district}&startDate=${year}-01-01&endDate=${year}-12-31`)
                        .then(resp => {
                            const events = result.data.instances;
                            const reports = resp.data.instances;
                            const temp = [];
                            const reportTemp = [];

                            const forums = formatDataGeneral(events, "Meeting Type", "Business Forum") || [];

                            forums.forEach((forum, idx) => {
                                const currentReport = reports.find(rep => rep.trackedEntity === forum.trackedEntity);
                                const reportLink = getFileLinkIfExist(reports, "f5aRBjgbRsa", forum.trackedEntity);

                                let followUp = "";
                                if (currentReport) {
                                    currentReport.dataValues.forEach((rep) => {
                                        if (rep.dataElement === "fRf6Lla04gE") {
                                            followUp = rep.value;
                                        }
                                    });
                                }

                                temp.push({
                                    key: forum.trackedEntity || idx,
                                    date: getAttributeValue("Date", forum),
                                    venue: getAttributeValue("Meeting Venue", forum),
                                    issues: getAttributeValue("Description", forum),
                                    female: getAttributeValue("DPAT |  No. of direct beneficiaries (female)", forum),
                                    male: getAttributeValue("DPAT |  No. of direct beneficiaries (male)", forum),
                                    report: reportLink
                                        ? `https://dddp.gov.gh/api/events/files?eventUid=${reportLink}&dataElementUid=f5aRBjgbRsa`
                                        : "Not Uploaded",
                                });

                                reportTemp.push({
                                    no: idx + 1,
                                    decision: getAttributeValue("Decision", forum),
                                    followUp: followUp || "Not Provided",
                                });
                            });

                            setData(temp);
                            setReport(reportTemp);

                            const reportsComplete = rowsHaveAllEvidenceLinks(temp, ["report"]);
                            setScorei(temp.length >= 2 && reportsComplete ? 1 : 0);
                            setScoreii(
                                scoreOnlyIfRowValuesProvided(reportTemp, ["followUp"], 1, { minRows: 1 }),
                            );

                        })
                        .catch(err => console.log(err))
                }

            })
            .catch(err => console.log(err))
    }, [district, year]);

    useDpatIndicatorDataSource({
        isAssessmentStarted,
        savedAssessmentData,
        assessmentStartLoading,
        loadFromDhis2,
        applySnapshot: (snap) => {
            if (snap.data) setData(snap.data);
            if (snap.report) {
                setReport(
                    snap.report.map((row) => ({
                        ...row,
                        followUp: row.followUp ?? row.actions ?? "Not Provided",
                    })),
                );
            }
            if (snap.scorei != null) setScorei(snap.scorei);
            if (snap.scoreii != null) setScoreii(snap.scoreii);
            if (snap.maxScore != null) setMaxScore(snap.maxScore);
        },
        deps: [year, district, loadFromDhis2],
    });

    const dataColumn = [
        {
            title: "Date",
            dataIndex: "date",
            key: "date"
        },
        {
            title: "Venue",
            dataIndex: "venue",
            key: "venue"
        },
        {
            title: "Issues discussed",
            dataIndex: "issues",
            key: "issues"
        },
        {
            title: "No. of Female Participants",
            dataIndex: "female",
            key: "female"
        },
        {
            title: "No. of Male Participants",
            dataIndex: "male",
            key: "male"
        },
        {
            title: "Report",
            dataIndex: "report",
            key: "report"
        }
    ];

    const reportColumn = [
        {
            title: "No.",
            dataIndex: "no",
            key: "no"
        },
        {
            title: "Decisions made",
            dataIndex: "decision",
            key: "decision"
        },
        {
            title: "Follow-up",
            dataIndex: "followUp",
            key: "followUp"
        }
    ];

    return (
        <Comment
            data={data}
            year={year}
            districtId={district}
            tableCommentedId={`sdi6.0-6.4-${year}`}
            hideComment={hideComment}
        >
            {({ renderCommentFeedbackFooter }) => (
          <DpatSubIndicatorSection code={DPAT_SLUG}>
                    <DpatIndicatorCmsIntro
                        year={year}
                        slug={DPAT_SLUG}
                        mainTitleStyle={{ marginTop: "30px" }}
                    />

                    <IndicatorScoreSection>
          <MaxScoreDisplay score={maxScore} />
                              <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 6.0-6.4 Actual Score:" score={scorei} />
                    <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 6.0-6.4 Actual Score:" score={scoreii} hideWhenFinal />
                    </IndicatorScoreSection>
                    <Title level={4} style={{ marginTop: "20px" }}>
                        I- Evidence of business foral/platform meetings held
                    </Title>
                    <Table
                        columns={dataColumn}
                        dataSource={transformDpatTableRows(data, ["report"], { report: "View Report" })}
                        pagination={false} bordered
                        rowKey="key"
                    />

                    <Title level={4} style={{ marginTop: "20px" }}>
                        II- Evidence of follow-up actions
                    </Title>
                    <Table
                        columns={reportColumn}
                        dataSource={report}
                        pagination={false} bordered
                    />
            {!hideComment && renderCommentFeedbackFooter()}
          </DpatSubIndicatorSection>
            )}
        </Comment>
    );
})

export default BusinessCommunityEngagement;